import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { BuilderMode, BuilderStep, SurveyFocus, SurveyForm, TemplatePreload, Question } from '../types';
import { surveyApi, USE_MOCK } from '@/api/client';
import type { PagedSurveyDto } from '@/api/types';
import { useOUItems, useGdprOuKey } from '@/hooks/useOUs';
import { buildOuTree, findOuNodePath, type OuNode } from '@/components/common/OuTree';
import {
  validateStepInfo,
  validateStepAudiencia,
  validateStepPreguntas,
  validateStepConfig,
} from '../validators';

/** Maps API questionType string back to builder Question type */
function fromBackendQuestionType(t: string): Question['type'] {
  switch (t) {
    case 'nps':  return 'nps';
    case 'open': return 'open';
    default:     return 'behaviors';
  }
}

/** Agrupa `ouIds` (planos, cualquier nivel) por su raíz real recorriendo el árbol completo ya
 *  cargado — reemplaza la heurística previa de coincidencia de prefijo de string (`startsWith`),
 *  que no es válida con ids reales tipo GUID. Un id que ya no existe en el catálogo actual se
 *  descarta silenciosamente (la OU fue eliminada), en vez de intentar reconciliarlo. */
export function splitOuIdsByRoot(
  tree: OuNode[],
  ouIds: string[],
  rootIds: ReadonlySet<string>,
): { audienceOUs: string[]; audienceSubOUs: Record<string, string[]> } {
  const audienceOUs: string[] = [];
  const audienceSubOUs: Record<string, string[]> = {};

  for (const ouId of ouIds) {
    if (rootIds.has(ouId)) {
      if (!audienceOUs.includes(ouId)) audienceOUs.push(ouId);
      if (!audienceSubOUs[ouId]) audienceSubOUs[ouId] = [];
      continue;
    }
    const nodePath = findOuNodePath(tree, ouId);
    if (!nodePath) continue;
    const rootNode = nodePath.find(pathNode => rootIds.has(pathNode.ou.id));
    if (!rootNode) continue;
    if (!audienceOUs.includes(rootNode.ou.id)) audienceOUs.push(rootNode.ou.id);
    const existingSubIds = audienceSubOUs[rootNode.ou.id] ?? [];
    audienceSubOUs[rootNode.ou.id] = [...existingSubIds, ouId];
  }

  return { audienceOUs, audienceSubOUs };
}

/** Reconstructs wizard audienceOUs + audienceSubOUs + destMode from the RN-009 DTO fields.
 * rootOuIds identifica cuáles ids de dto.ouIds son OUs raíz (type === 'OU' en Mongo) — ya no
 * es una lista fija de slugs (E-003: los ids reales son GUIDs de Azure AD/Mongo). */
function audienceContractToFormFields(dto: PagedSurveyDto, rootOuIds: ReadonlySet<string>, tree: OuNode[]): Partial<SurveyForm> {
  if (dto.audienceType === 'meeting') {
    return { destMode: 'meeting', sessionId: dto.targetMeetingId ?? '', destPeople: [], destOU: '', audienceOUs: [], audienceSubOUs: {} };
  }
  if (dto.audienceType === 'people') {
    return { destMode: 'people', sessionId: '', destPeople: dto.targetUserIds ?? [], destOU: '', audienceOUs: [], audienceSubOUs: {} };
  }
  if (dto.audienceType === 'ou' && dto.category === 'personalizada') {
    return { destMode: 'ou', destOU: dto.ouIds?.[0] ?? '', sessionId: '', destPeople: [], audienceOUs: [], audienceSubOUs: {} };
  }
  // audienceType === 'ou' (general survey)
  const ouIds = dto.ouIds ?? [];
  if (ouIds.length === 0 || ouIds.includes('global')) {
    return { audienceOUs: [], audienceSubOUs: {} };
  }
  return splitOuIdsByRoot(tree, ouIds, rootOuIds);
}

/** Populates SurveyForm from a fetched SurveyDto */
function surveyDtoToForm(dto: PagedSurveyDto, current: SurveyForm, rootOuIds: ReadonlySet<string>, tree: OuNode[]): SurveyForm {
  const questions: Question[] = dto.questions
    .sort((a, b) => a.order - b.order)
    .map(q => ({
      id: q.id,
      type: fromBackendQuestionType(q.questionType),
      enfoque: (q.focusType as SurveyFocus) ?? 'individual',
      presentMode: (q.presentationMode as 'matrix' | 'individual') ?? 'matrix',
      fundamental: q.fundamentalId ?? '',
      coreBehavior: q.dailyHabitId ?? '',
      text: q.text['es'] ?? Object.values(q.text)[0] ?? '',
      icon: '',
      options: [],
      includeNoObs: false,
    }));

  // Rebuild translations map: { lang: { "q_0": text, "q_1": text, ... } }
  // Keys must match the index-based keys that TranslationsSection writes ("q_0", "q_1", etc.).
  // Skip entries equal to the Spanish text — those are old fallback artifacts from a prior bug.
  const translations: Record<string, Record<string, string>> = {};
  const translationsEnabled: Record<string, boolean> = {};
  const sortedForTranslations = [...dto.questions].sort((a, b) => a.order - b.order);
  for (let qi = 0; qi < sortedForTranslations.length; qi++) {
    const q = sortedForTranslations[qi];
    const esText = (q.text['es'] as string | undefined) ?? '';
    for (const [lang, text] of Object.entries(q.text)) {
      if (lang === 'es') continue;
      if ((text as string) === esText) continue; // old fallback artifact, not a real translation
      if (!translations[lang]) translations[lang] = {};
      translations[lang][`q_${qi}`] = text as string;
      translationsEnabled[lang] = true;
    }
  }

  // Rebuild survey_name and survey_desc translation keys from backend metadata fields.
  for (const [lang, text] of Object.entries(dto.nameTranslations ?? {})) {
    if (!translations[lang]) translations[lang] = {};
    translations[lang]['survey_name'] = text;
    translationsEnabled[lang] = true;
  }
  for (const [lang, text] of Object.entries(dto.descriptionTranslations ?? {})) {
    if (!translations[lang]) translations[lang] = {};
    translations[lang]['survey_desc'] = text;
    translationsEnabled[lang] = true;
  }

  return {
    ...current,
    name: dto.name,
    description: dto.description ?? '',
    surveyType: (dto.category as SurveyForm['surveyType']) ?? current.surveyType,
    ...audienceContractToFormFields(dto, rootOuIds, tree),
    startDate: dto.startDate ? dto.startDate.split('T')[0] : current.startDate,
    expirationDate: dto.expiresAt ? dto.expiresAt.split('T')[0] : '',
    questions,
    translations,
    translationsEnabled,
    campaignWeight: dto.campaignWeight ?? current.campaignWeight,
    activeDays: dto.activeDays ?? current.activeDays,
    boostUrgente: dto.boostUrgente ?? current.boostUrgente,
    boostDays: dto.boostDays ?? current.boostDays,
    minRespondents: dto.minRespondents ?? current.minRespondents,
    maxRespondents: dto.maxRespondents ?? current.maxRespondents,
  };
}

const MIN_PREVIEW = 240;
const MAX_PREVIEW = 720;
const DEFAULT_PREVIEW = 420;

export function useSurveyBuilder(mode: BuilderMode, onClose: () => void, template?: TemplatePreload, surveyId?: string) {
  const { t } = useTranslation();

  // Resolve the ID to load when in edit mode
  const editId = surveyId ?? (template?.mode === 'edit' ? String(template.id) : undefined);

  // activeOnly: false — igual que useGdprOuKey(), esto reconstruye la audiencia YA GUARDADA de la
  // encuesta (no el picker en vivo, que tiene su propio useOUs() en StepAudiencia.tsx), así que no
  // debe depender del estado activo/inactivo actual de la OU o se perdería en silencio del audienceOUs
  // reconstruido una OU desactivada después de crear la encuesta.
  const { data: allOuItems = [] } = useOUItems({ activeOnly: false });
  const gdprOuKey = useGdprOuKey();
  const rootOuIds = useMemo(
    () => new Set(allOuItems.filter(item => item.type === 'OU').map(item => item.id)),
    [allOuItems],
  );
  const ouTree = useMemo(() => buildOuTree(allOuItems), [allOuItems]);

  const { data: loadedSurvey } = useQuery({
    queryKey: ['survey', editId],
    queryFn: () => surveyApi.getSurveyById(editId!),
    enabled: !USE_MOCK && !!editId,
    staleTime: Infinity,
  });

  const [step, setStep] = useState<BuilderStep>(0);
  const [form, setForm] = useState<SurveyForm>(() => ({
    name: template ? template.name : '',
    description: template ? template.description : '',
    surveyType: template ? 'general' : '',
    startDate: template?.startDate ? template.startDate.split('T')[0] : '',
    expirationDate: template?.expirationDate ? template.expirationDate.split('T')[0] : '',
    activeDays: [0, 1, 2, 3, 4],
    audienceOUs: [],
    audienceSubOUs: {},
    anonymous: false,
    campaignWeight: 5,
    boostUrgente: false,
    boostDays: 3,
    minRespondents: 3,
    maxRespondents: 10,
    questions: [],
    translations: {},
    translationsEnabled: {},
    destMode: 'meeting',
    sessionId: '',
    destPeople: [],
    destOU: '',
  }));

  // Populate form once the existing survey is fetched (edit mode)
  useEffect(() => {
    if (loadedSurvey) {
      setForm(current => surveyDtoToForm(loadedSurvey, current, rootOuIds, ouTree));
    }
  }, [loadedSurvey, rootOuIds, ouTree]);

  const [showExitModal, setShowExitModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [previewIconTarget, setPreviewIconTarget] = useState<number | null>(null);
  const [enfoqueFilter, setEnfoqueFilter] = useState<'all' | 'individual' | 'colectivo'>('all');
  const [previewVisible, setPreviewVisible] = useState(true);
  const [previewWidth, setPreviewWidth] = useState(DEFAULT_PREVIEW);

  const dragging = useRef(false);
  const startX = useRef(0);
  const startW = useRef(DEFAULT_PREVIEW);
  const handleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      const delta = startX.current - e.clientX;
      const newW = Math.min(MAX_PREVIEW, Math.max(MIN_PREVIEW, startW.current + delta));
      setPreviewWidth(newW);
    };
    const onMouseUp = () => {
      if (!dragging.current) return;
      dragging.current = false;
      handleRef.current?.classList.remove('dragging');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const onHandleMouseDown = useCallback((e: React.MouseEvent) => {
    dragging.current = true;
    startX.current = e.clientX;
    startW.current = previewWidth;
    handleRef.current?.classList.add('dragging');
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    e.preventDefault();
  }, [previewWidth]);

  const stepValidators = useMemo<Array<() => string[]>>(() => [
    () => validateStepInfo(form, t),
    () => validateStepAudiencia(form, t),
    () => validateStepPreguntas(form, t),
    () => validateStepConfig(form, t, gdprOuKey),
  ], [form, t, gdprOuKey]);

  const goNext = useCallback(() => {
    const validator = stepValidators[step];
    if (validator !== undefined) {
      const errors = validator();
      if (errors.length > 0) { setValidationErrors(errors); return; }
    }
    if (step < 4) setStep((step + 1) as BuilderStep);
  }, [step, stepValidators]);

  const goPrev = useCallback(() => {
    if (step > 0) setStep((step - 1) as BuilderStep);
  }, [step]);

  const handleClose = useCallback(() => setShowExitModal(true), []);
  const handleExitCancel = useCallback(() => setShowExitModal(false), []);
  const handleExitConfirm = useCallback(() => onClose(), [onClose]);
  const clearValidationErrors = useCallback(() => setValidationErrors([]), []);

  return {
    // state
    step, setStep,
    form, setForm,
    showExitModal,
    validationErrors,
    previewIconTarget, setPreviewIconTarget,
    enfoqueFilter, setEnfoqueFilter,
    previewVisible, setPreviewVisible,
    previewWidth,
    handleRef,
    // derived
    isLast: step === 4,
    editId,
    mode,
    // handlers
    goNext, goPrev,
    handleClose, handleExitCancel, handleExitConfirm,
    clearValidationErrors,
    onHandleMouseDown,
  };
}
